/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Toolbar,
    TOOLBAR_ITEM_TYPE,
    toolbarItemDecorators,
    useGridApi,
    useModal,
} from '@veeam-vspc/components';

import type { ToolbarItems } from '@veeam-vspc/components';
import type { ResellerCloudVcdResourceMapModel } from '@veeam-vspc/models/web-controllers';

import { useLang } from 'views/providers/LangProvider/hooks';
import { canRemoveResource } from '../../../../utils';
import { VcdResourceDialog } from '../VcdResourceDialog';
import addIcon from 'images/actions/add.svg';
import removeIcon from 'images/actions/remove.svg';
import editIcon from 'images/actions/edit.svg';

interface Props {
    items: ResellerCloudVcdResourceMapModel[];
    cloudConnectAgentUids: string[];
}

export const ActionsToolbar = ({ items, cloudConnectAgentUids }: Props) => {
    const lang = useLang();
    const gridApi = useGridApi<ResellerCloudVcdResourceMapModel, void, void>();

    const [quotaDialog, quotaDialogActions] = useModal<ResellerCloudVcdResourceMapModel>({
        render: ({ deactivate, data }) => (
            <VcdResourceDialog
                data={data}
                existingItems={items}
                cloudConnectAgentUids={cloudConnectAgentUids}
                onConfirm={(newData) => {
                    if (data) {
                        items[items.findIndex(item => item.cloudConnectResourceUid === data.cloudConnectResourceUid)] = newData;
                    } else {
                        items.push(newData);
                    }
                    gridApi.reloadGrid();
                }}
                deactivate={deactivate}
            />
        ),
    });

    const toolbarItems: ToolbarItems = [
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: addIcon,
            text: lang.ADD,
            onClick: () => {
                quotaDialogActions.activate();
            },
            decorators: [
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: editIcon,
            text: lang.EDIT,
            onClick: async() => {
                const selected = gridApi.selected[0];
                quotaDialogActions.activate(selected);
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: removeIcon,
            text: lang.REMOVE,
            onClick: async() => {
                const selected = gridApi.selected[0];
                if (
                    await canRemoveResource(
                        selected,
                        lang.CANNOT_REMOVE_VMWARE_CLOUD_DIRECTOR_RESOURCES,
                        lang.THIS_ACTION_WILL_REMOVE_VMWARE_CLOUD_DIRECTOR,
                        lang
                    )
                ) {
                    items.splice(items.findIndex(item => item.cloudConnectResourceUid === selected.cloudConnectResourceUid), 1);
                    gridApi.reloadGrid();
                }
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
    ];

    return (
        <>
            <Toolbar items={toolbarItems} />
            {quotaDialog}
        </>
    );
};
